// SocketContext.jsx
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';

const SocketContext = createContext(null);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket doit être utilisé dans un SocketProvider');
  }
  return context;
};

/**
 * Nettoie et valide l'URL fournie via env var.
 * - Si URL invalide ou contient des caractères suspects (***, espaces, etc.) -> fallback sur localhost.
 */
function getValidatedServerUrl() {
  const raw = import.meta.env.VITE_API_URL || 'http://localhost:3002';

  // retire les espaces et les sequences d'asterisques évidentes
  let sanitized = String(raw).trim().replace(/\*+/g, '');

  // Si la string est vide après nettoyage, fallback
  if (!sanitized) return 'http://localhost:3002';

  // Si l'utilisateur a fourni une URL avec un path contenant des segments non valides
  // on essaie de construire un objet URL ; si ça échoue, fallback.
  try {
    // Si l'env est juste un hostname sans protocole, ajoute http://
    const maybeProtocol = sanitized.match(/^https?:\/\//) ? sanitized : `http://${sanitized}`;
    const u = new URL(maybeProtocol);

    // Recompose URL sans trailing slash
    return u.origin;
  } catch (e) {
    console.warn('VITE_API_URL invalide — fallback sur http://localhost:3002', raw, e);
    return 'http://localhost:3002';
  }
}

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Récupérer le token depuis localStorage (on peut changer pour context/état global si besoin)
    const token = localStorage.getItem('token');

    if (!token) {
      console.log('🔌 Socket.IO: Pas de token trouvé, connexion ignorée');
      return;
    }

    const SERVER_URL = getValidatedServerUrl();
    console.log('🔌 Socket.IO: tentative de connexion vers', SERVER_URL);

    let newSocket;
    try {
      newSocket = io(SERVER_URL, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        // path peut être défini si ton serveur utilise un path custom, par ex: '/socket.io'
        // path: '/socket.io',
      });
    } catch (err) {
      console.error('Erreur lors de la création du client Socket.IO:', err);
      toast.error('Impossible d’instancier Socket.IO (URL invalide). Vérifie VITE_API_URL.');
      return;
    }

    // Handlers
    const handleConnect = () => {
      console.log('✅ Socket.IO connecté:', newSocket.id);
      setIsConnected(true);
      toast.success('Connexion temps réel établie');
    };

    const handleDisconnect = (reason) => {
      console.log('❌ Socket.IO déconnecté:', reason);
      setIsConnected(false);
      // warning local pour info
      if (reason === 'io server disconnect') {
        // si le serveur a explicitly déconnecté, le client ne se reconnecte pas automatiquement sauf si on appelle connect()
        console.warn('Le serveur a déconnecté le client explicitement.');
      }
    };

    const handleConnectError = (error) => {
      // error peut être un objet Error ou un string
      const msg = error?.message || String(error);
      console.error('❌ Erreur connexion Socket.IO:', msg, error);
      setIsConnected(false);

      // message d'aide spécifique si on détecte une erreur de namespace classique
      if (msg.includes('Invalid namespace') || msg.includes('namespace')) {
        toast.error('Erreur Socket.IO: namespace invalide détecté. Vérifie VITE_API_URL ou l’URL fournie.');
        // Log plus détaillé pour debugging
        console.error('Vérifie la valeur de VITE_API_URL et qu’aucun segment "***" ou path invalide n’est présent.');
      } else {
        toast.error(`Erreur connexion temps réel: ${msg}`);
      }
    };

    const handleError = (err) => {
      console.error('Socket.IO error event:', err);
    };

    newSocket.on('connect', handleConnect);
    newSocket.on('disconnect', handleDisconnect);
    newSocket.on('connect_error', handleConnectError);
    newSocket.on('error', handleError);

    // exemple d'écoute "annotation:mentioned"
    const onMention = (data) => {
      console.log('📩 Mention reçue:', data);
      toast((t) => (
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-semibold">
              {data?.author?.firstName?.charAt(0) ?? '?'}
            </div>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">
              {(data?.author?.firstName ?? '') + ' ' + (data?.author?.lastName ?? '')} vous a mentionné
            </p>
            <p className="text-xs text-gray-600 truncate max-w-xs">
              {data?.annotation?.content ?? ''}
            </p>
          </div>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              if (data?.projectId && data?.annotation?.id) {
                window.location.href = `/projects/${data.projectId}#annotation-${data.annotation.id}`;
              }
            }}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Voir
          </button>
        </div>
      ), {
        duration: 8000,
        icon: '💬',
        position: 'top-right'
      });
    };

    newSocket.on('annotation:mentioned', onMention);

    // expose socket
    setSocket(newSocket);

    // cleanup
    return () => {
      console.log('🔌 Fermeture connexion Socket.IO (cleanup)');
      try {
        newSocket.off('connect', handleConnect);
        newSocket.off('disconnect', handleDisconnect);
        newSocket.off('connect_error', handleConnectError);
        newSocket.off('error', handleError);
        newSocket.off('annotation:mentioned', onMention);
        newSocket.disconnect(); // utilise disconnect() pour bien stopper la reconnexion automatique
      } catch (e) {
        console.warn('Erreur durant le cleanup Socket.IO', e);
      }
    };
    // Intentionnellement, on ne met pas de dépendances (on veut exécuter une seule connexion au montage).
    // Si tu veux reconnecter quand le token change -> décommenter token dans le tableau de dépendances.
  }, []); // si tu veux que la socket se recrée quand token change, remplacer par [/* token */]

  const joinProject = useCallback((projectId) => {
    if (!projectId) {
      console.warn('joinProject: projectId manquant');
      return;
    }
    if (socket && isConnected) {
      socket.emit('join_project', projectId);
      console.log(`📁 Rejoint projet: ${projectId}`);
    } else {
      console.warn('joinProject: socket non connecté (attendre la connexion)');
    }
  }, [socket, isConnected]);

  const leaveProject = useCallback((projectId) => {
    if (!projectId) {
      console.warn('leaveProject: projectId manquant');
      return;
    }
    if (socket && isConnected) {
      socket.emit('leave_project', projectId);
      console.log(`📤 Quitté projet: ${projectId}`);
    } else {
      console.warn('leaveProject: socket non connecté (impossible de quitter)');
    }
  }, [socket, isConnected]);

  const value = {
    socket,
    isConnected,
    joinProject,
    leaveProject
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
